export const legalEn = {
  footer: { privacy: "Privacy Policy", terms: "Terms of Use", deleteAccount: "Delete Account" },
  privacy: {
    meta: {
      title: "Privacy Policy | GetTaxcy",
      description: "How GetTaxcy collects, uses and protects personal data of passengers and drivers under the EU GDPR.",
    },
    md: `
# GetTaxcy Privacy Policy
Last updated: 3 November 2026
GetTaxcy ("we", "us") runs the GetTaxcy passenger app, the GetTaxcy Driver app and the website gettaxcy.com. This policy explains what personal data we collect, why, and your rights under the EU General Data Protection Regulation (GDPR).
## Who we are
Data controller: Georgios Kesidis, trading as GetTaxcy
Address: Diagorou 20, Kato Paphos 8042, Cyprus
Email: costataxicy@gmail.com
Phone / WhatsApp: +357 99940234
## What data we collect
**Passengers**
- Account details: name, phone number, email address
- Location: pickup and drop-off points and your device location while you use the app to book or follow a ride
- Ride history: routes, dates, times, prices, driver and vehicle details
- Payment information: card payments are processed by Stripe. We do not see or store your full card number.
- Messages and ratings you send through the app
**Drivers**
- Account details: name, phone number, email address, profile photo
- Driver documents: driving and taxi licence, vehicle details and registration, insurance, and identity checks we need to approve you
- Location: your device location while you are online in the driver app, including when the app is in the background, so we can send you nearby rides and show passengers where you are
- Earnings, commissions and payout details. Payouts are handled through Stripe Connect.
**Everyone**
- Device and technical data: device type, operating system, app version, crash logs and push notification tokens
## Why we use your data
- To create and manage your account
- To match passengers with drivers, show live trip location and complete rides
- To take payments, pay drivers and keep financial records
- For safety, support, dealing with complaints, and preventing fraud
- To send ride updates and service messages
- To meet our legal and tax obligations
Our legal bases are: performing our contract with you, our legitimate interests (safety, fraud prevention, improving the service), legal obligations, and your consent where we ask for it (for example location permission and marketing). You can withdraw consent at any time.
## Who we share data with
- **Between passengers and drivers:** the details needed for a ride (first name, pickup point, phone contact, vehicle and live location)
- **Stripe:** card payments and driver payouts
- **Google Maps:** maps, addresses and routes
- **Hosting, push notification and app infrastructure providers** that run the apps for us
- **Authorities:** when the law requires it
We do not sell your personal data.
Some of these providers may process data outside the EU. When they do, it is protected by appropriate safeguards such as EU Standard Contractual Clauses.
## How long we keep data
We keep your account data while your account is open. Ride and payment records are kept for as long as Cypriot tax and accounting law requires (normally up to 6 years). Other data is deleted or anonymised when it is no longer needed.
## Your rights
You have the right to access, correct, delete, or receive a copy of your data, to object to or restrict how we use it, and to withdraw consent. To use these rights, email costataxicy@gmail.com.
You can also complain to the Commissioner for Personal Data Protection in Cyprus (www.dataprotection.gov.cy).
## Deleting your account
You can delete your account in the app (Profile → Delete account) or by requesting it at gettaxcy.com/delete-account or by email. We delete your account within 30 days. We may keep the ride and payment records we are legally required to keep.
## Children
GetTaxcy accounts are for people aged 18 and over.
## Security
We use encryption and access controls to protect your data. No system is 100% secure, but we work to keep your data safe.
## Changes
If we change this policy, we will update the date at the top and tell you about important changes in the app.
`,
  },
  terms: {
    meta: {
      title: "Terms of Use | GetTaxcy",
      description: "Terms of Use for the GetTaxcy passenger app and the GetTaxcy Driver app in Cyprus.",
    },
    md: `
# GetTaxcy Terms of Use
Last updated: 3 November 2026
These Terms of Use apply to the GetTaxcy passenger app, the GetTaxcy Driver app and the website gettaxcy.com. By creating an account or using GetTaxcy, you agree to these terms.
## Who we are
GetTaxcy is run by Georgios Kesidis, trading as GetTaxcy.
Address: Diagorou 20, Kato Paphos 8042, Cyprus
Email: costataxicy@gmail.com
Phone / WhatsApp: +357 99940234
## Who can use GetTaxcy
You must be at least 18 years old to create a GetTaxcy account. You must give accurate details, keep your login secure and you are responsible for activity on your account.
## Our service
GetTaxcy is a platform that connects passengers with independent licensed drivers. Drivers are not employees of GetTaxcy. They carry out the rides themselves and are responsible for holding the licences, insurance and vehicle documents required by law.
## Fares and payment
- The fare is shown in the app before you confirm a booking.
- You can pay by card through Stripe or in cash directly to the driver.
- Card payments are processed by Stripe. We do not see or store your full card number.
## Cancellations and no-shows
Cancellation fees may apply if you cancel a ride after a driver has been assigned. A no-show fee may apply if the driver arrives at the pickup point and you do not show up.
## For drivers
- GetTaxcy charges a commission of 12% of the fare for each ride.
- Driver earnings are paid out weekly through Stripe Connect.
- Drivers must keep their driving and taxi licence, vehicle registration and insurance valid at all times.
## Behaviour
Passengers and drivers must treat each other with respect and act lawfully at all times. Violence, threats, harassment, discrimination, damage to vehicles and any illegal activity are not allowed.
## Suspension of accounts
We may suspend or close accounts that break these terms or the law, or that put the safety of others at risk. You can delete your account at any time at gettaxcy.com/delete-account.
## Limitation of liability
To the extent permitted by law, GetTaxcy is not liable for indirect or consequential losses, or for losses caused by the acts or omissions of drivers, passengers or third parties. Nothing in these terms limits liability that cannot be limited by law, including liability for death or personal injury caused by our negligence, or your statutory rights as a consumer.
## Governing law
These terms are governed by the laws of the Republic of Cyprus. The courts of Cyprus have jurisdiction over any dispute.
## Changes to these terms
We may update these terms from time to time. If we make important changes, we will update the date at the top and tell you in the app. If you keep using GetTaxcy after a change, you accept the updated terms.
## Contact
Questions about these terms: costataxicy@gmail.com or +357 99940234.
`,
  },
  deletion: {
    meta: {
      title: "Delete your account | GetTaxcy",
      description: "How to delete your GetTaxcy passenger or driver account and personal data.",
    },
    md: `
# Delete your account
You can delete your GetTaxcy account at any time, whether you use the GetTaxcy passenger app or the GetTaxcy Driver app.
## Option 1 – In the app
Open the app and go to **Profile → Delete account**.
## Option 2 – Send us a request
Send a request from your registered phone number by email to costataxicy@gmail.com or on WhatsApp to +357 99940234. You can also use the form on this page.
## What happens next
- We delete your account and personal data within 30 days.
- Ride and payment records that the law requires us to keep (up to 6 years) are kept for that period.
`,
    form: {
      title: "Request account deletion",
      name: "Full name",
      phone: "Registered phone number",
      email: "Email",
      role: "Account type",
      passenger: "Passenger",
      driver: "Driver",
      reason: "Reason (optional)",
      submit: "Send deletion request",
      sending: "Sending…",
      success: "Thank you. We have received your request and will delete your account and personal data within 30 days.",
      error: "Something went wrong. Please try again, or email costataxicy@gmail.com.",
      required: "Please fill in your name, phone number and a valid email.",
    },
  },
};
